import React, { useRef, useEffect, useState } from 'react';
import { useGridApiContext, GridFilterPanel } from '@mui/x-data-grid-premium';
import { deleteFilledSvg, deleteOutlineSvg, checkIconSvg } from '../customIcons';
import CloseIcon from '@mui/icons-material/Close';
import styles from './CustomGridFilterPanel.module.css';
import { Tooltip } from '@airbus/components-react';
import {
  restoreHeaderFooterZ,
  replaceFilterDeleteIcons,
  handleFilterDelete,
  startDrag,
  moveDrag,
  attachTooltip,
} from '../../utils/gridFilterPanelUtils';

const FILTER_PANEL_CLASS = 'custom-mui-filter-panel';

function CustomGridFilterPanel(props) {
  const apiRef = useGridApiContext();
  const panelRef = useRef<HTMLDivElement>(null);

  const toolbarHostRef = useRef<HTMLDivElement | null>(null);
  const [panelTooltip, setPanelTooltip] = useState({
    open: false,
    label: '',
    left: 0,
    top: 0,
    placement: 'bottom' as 'bottom' | 'top' | 'left' | 'right',
  });

  type TooltipR<T> = Partial<T> & Record<string, unknown>;

  const headerRef = useRef<HTMLElement | null>(null);
  const footerRef = useRef<HTMLElement | null>(null);
  const dragState = useRef({
    isDragging: false,
    dragStart: { x: 0, y: 0, left: 0, top: 0 },
  });

  const onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) return;
    const panel = panelRef.current;
    if (!panel) return;

    if (!(e.target as HTMLElement)?.classList.contains('draggable-filter-header')) return;

    startDrag(e.clientX, e.clientY, panel, dragState);
  };

  const onMouseMove = (e: MouseEvent) => {
    const panel = panelRef.current;
    if (!panel) return;

    moveDrag(
      e.clientX,
      e.clientY,
      panel,
      dragState,
      headerRef.current ?? undefined,
      footerRef.current ?? undefined,
    );
  };

  const onMouseUp = () => {
    dragState.current.isDragging = false;
    document.body.style.userSelect = '';
    restoreHeaderFooterZ(headerRef.current ?? undefined, footerRef.current ?? undefined);
  };

  const onTouchStart = (e: TouchEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    const target = e.target as HTMLElement;
    if (!target.classList.contains('draggable-filter-header')) return;

    const touch = e.touches[0];
    startDrag(touch.clientX, touch.clientY, panel, dragState);
  };

  const onTouchMove = (e: TouchEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    const touch = e.touches[0];
    moveDrag(
      touch.clientX,
      touch.clientY,
      panel,
      dragState,
      headerRef.current ?? undefined,
      footerRef.current ?? undefined,
    );
  };

  const closePanel = (reason = 'close') => {
    const panel = panelRef.current;
    if (panel) panel.dataset.closeReason = reason;

    try {
      apiRef.current?.hideFilterPanel?.();
    } catch (err) {
      console.error(err);
    }

    if (panel?.parentNode === document.body) {
      panel.remove();
    }
  };

  useEffect(() => {
    document.querySelectorAll(`.${FILTER_PANEL_CLASS}`).forEach((el) => {
      if (el.parentNode) el.remove();
    });
  }, []);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    panel.classList.add(FILTER_PANEL_CLASS);

    if (panel.parentNode !== document.body) {
      document.body.appendChild(panel);
    }

    if (window.innerWidth <= 400) {
      Object.assign(panel.style, {
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        transform: 'none',
        borderRadius: '0',
      });
    } else {
      Object.assign(panel.style, {
        top: '10%',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '90%',
        maxWidth: '625px',
        minWidth: '280px',
        borderRadius: '8px',
      });

      headerRef.current = document.querySelector('.header_header__Xb1tx') as HTMLElement | null;
      footerRef.current = document.querySelector('.footer_footerPage__ScztE') as HTMLElement | null;

      panel.addEventListener('mousedown', onMouseDown);
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
      panel.addEventListener('touchstart', onTouchStart, { passive: false });
      window.addEventListener('touchmove', onTouchMove, { passive: false });

      return () => {
        panel.removeEventListener('mousedown', onMouseDown);
        window.removeEventListener('mousemove', onMouseMove);
        window.removeEventListener('mouseup', onMouseUp);

        panel.removeEventListener('touchstart', onTouchStart);
        window.removeEventListener('touchmove', onTouchMove);
        restoreHeaderFooterZ(headerRef.current, footerRef.current);
        if (panel.parentNode === document.body) {
          panel.remove();
        }
      };
    }
  }, []);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const footer = panel?.querySelector('.MuiDataGrid-panelFooter');

    const getIsMobile = () => globalThis.matchMedia('(max-width:600px)').matches;

    replaceFilterDeleteIcons(panel, getIsMobile(), deleteOutlineSvg, deleteFilledSvg);

    const observer = new MutationObserver(() => {
      replaceFilterDeleteIcons(panel, getIsMobile(), deleteOutlineSvg, deleteFilledSvg);
    });

    observer.observe(panel, { childList: true, subtree: true });

    if (footer && !footer.querySelector('.custom-done-btn')) {
      const btn = document.createElement('button');
      btn.innerHTML = `${checkIconSvg}Done`;
      btn.className =
        'MuiButtonBase-root MuiButton-root MuiButton-text MuiButton-sizeSmall custom-done-btn';
      btn.onclick = () => {
        const p = panelRef.current;
        if (p) p.dataset.closeReason = 'done';
        try {
          apiRef.current?.hideFilterPanel?.();
        } catch {}
        if (p?.parentNode === document.body) {
          p.remove();
        }
      };
      footer.appendChild(btn);
    }

    const handleDelete = (e: MouseEvent | TouchEvent) => handleFilterDelete(e, panel, apiRef);

    panel.addEventListener('click', handleDelete);
    panel.addEventListener('touchend', handleDelete, { passive: true });
    return () => {
      panel.removeEventListener('click', handleDelete);
      panel.removeEventListener('touchend', handleDelete);
      observer.disconnect();
    };
  }, [apiRef]);

  // Disable MUI tooltips and title attributes inside the filter panel
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    panel.querySelectorAll('.MuiTooltip-popper, .MuiTooltip-root').forEach((n) => {
      (n as HTMLElement).style.display = 'none';
    });

    panel.querySelectorAll('[title]').forEach((el) => el.removeAttribute('title'));

    const obs = new MutationObserver(() => {
      panel.querySelectorAll('[title]').forEach((el) => el.removeAttribute('title'));
      panel.querySelectorAll('.MuiTooltip-popper, .MuiTooltip-root').forEach((n) => {
        (n as HTMLElement).style.display = 'none';
      });
    });
    obs.observe(panel, { childList: true, subtree: true });

    return () => obs.disconnect();
  }, []);

  // -----------------------------
  // Tooltip for Delete & Clear icons inside filter panel
  // -----------------------------

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    attachTooltip(panel, toolbarHostRef, setPanelTooltip);

    const observer = new MutationObserver(() => {
      attachTooltip(panel, toolbarHostRef, setPanelTooltip);
    });

    observer.observe(panel, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      setPanelTooltip((s) => ({ ...s, open: false }));
    };
  }, []);

  return (
    <div
      className={`${styles.filterPanel} Muifilterparent-class ${FILTER_PANEL_CLASS}`}
      ref={panelRef}
    >
      <div className={`draggable-filter-header ${styles.draggableHeader}`}>
        Filter {''}
        <button
          type='button'
          aria-label='Close filter panel'
          onClick={() => closePanel('click-x')}
          className={styles.filterCloseBtn}
        >
          <CloseIcon fontSize='small' />
        </button>
      </div>
      <div className={styles.filterBody} ref={toolbarHostRef}>
        <GridFilterPanel
          {...props}
          sx={(theme) => ({
            '& .MuiDataGrid-panelContent': {
              [theme.breakpoints.down('sm')]: {
                maxHeight: '100%',
              },
            },

            '& .MuiDataGrid-filterForm': {
              display: 'flex',
              flexWrap: {
                xs: 'wrap',
                sm: 'nowrap',
              },
              gap: '4px',
            },
            '&& .MuiDataGrid-panelWrapper .MuiDataGrid-filterForm': {
              padding: '8px',
            },
            '& .MuiDataGrid-filterFormLogicOperatorInput': {
              [theme.breakpoints.down('sm')]: {
                flex: '0 0 65px',
                minWidth: '65px',
                marginBottom: 0,
              },
            },

            '& .MuiDataGrid-filterFormColumnInput': {
              flex: 1,
              [theme.breakpoints.down('sm')]: {
                minWidth: '100px',
              },
            },

            '& .MuiDataGrid-filterFormOperatorInput': {
              flex: 1,
              [theme.breakpoints.down('sm')]: {
                minWidth: '100px',
              },
            },

            '& .MuiDataGrid-filterFormValueInput': {
              flex: 1,
              width: { sm: '150px' },
              [theme.breakpoints.down('sm')]: {
                minWidth: '150px',
              },

              '& .MuiAutocomplete-endAdornment, & .MuiInputAdornment-root': {
                display: 'inline-flex',
                alignItems: 'center',
              },

              '& .MuiChip-root .MuiChip-deleteIcon': {
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 140ms linear',
              },
              '& .MuiChip-root:hover .MuiChip-deleteIcon': {
                opacity: 1,
                pointerEvents: 'auto',
              },
            },

            '& .MuiDataGrid-filterFormDeleteIcon': {
              flex: '0 0 50px',
              minWidth: 50,
              justifyContent: 'flex-end',
            },

            '& .MuiDataGrid-panelFooter': {
              borderRadius: '0px',
              justifyContent: 'space-between',

              '& button:not(:first-of-type)': {
                position: 'relative',
                paddingLeft: '12px',
                marginLeft: '12px',
              },

              [theme.breakpoints.down('sm')]: {
                position: 'absolute',
                bottom: 18,
                left: 16,
                right: 16,
                background: '#fff',
                border: '1px solid #00205B',
                justifyContent: 'space-evenly',
                borderRadius: '5px',
                margin: '15px 0 0',

                '& button:not(:first-of-type)::before': {
                  content: '""',
                  position: 'absolute',
                  left: -2,
                  top: '25%',
                  bottom: '25%',
                  width: '1px',
                  backgroundColor: '#00205B',
                },
              },
            },
          })}
        />
      </div>

      {panelTooltip.open && (
        <div
          style={{
            position: 'absolute',
            left: panelTooltip.left,
            top: panelTooltip.top + 18,
            pointerEvents: 'none',
          }}
          aria-hidden
        >
          <Tooltip
            {...({
              label: panelTooltip.label,
              open: true,
              placement: panelTooltip.placement,
            } as TooltipR<React.ComponentProps<typeof Tooltip>>)}
          >
            <span style={{ display: 'inline-block', width: 8, height: 8, pointerEvents: 'none' }} />
          </Tooltip>
        </div>
      )}
    </div>
  );
}

export default CustomGridFilterPanel;
